import classNames from 'classnames';
import React, { useCallback } from 'react';
import type { FilterListItem as FilterListOption } from 'utils/filter-list';
import styles from './FilterList.module.scss';

export interface OptionMeta {
  isInFocus: boolean;
  isSelected: boolean;
  isDisabled?: boolean;
  hint?: string;
  hintTooltip?: React.ReactNode;
}

interface Props extends OptionMeta {
  onClick: (event: React.MouseEvent<HTMLElement>, item: FilterListOption) => void;
  onMouseEnter: (event: React.MouseEvent<HTMLElement>, item: FilterListOption) => void;
  item: FilterListOption;
  subItems?: Array<JSX.Element>;
  renderMethod: (item: FilterListOption, meta: OptionMeta) => JSX.Element;
  singleSelect?: boolean;
}

const FilterListItem: React.FC<Props> = ({
  hint,
  hintTooltip,
  isDisabled: propIsDisabled,
  isInFocus,
  isSelected,
  item,
  onClick,
  onMouseEnter,
  renderMethod,
  singleSelect,
  subItems,
}) => {
  const isDisabled = !!(item.disabled || propIsDisabled);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      if (!isDisabled && onClick) {
        onClick(event, item);
      }
    },
    [isDisabled, onClick, item]
  );

  const handleMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      if (onMouseEnter) onMouseEnter(event, item);
    },
    [onMouseEnter, item]
  );

  const liClass = classNames(styles.filterListItem, {
    [styles.isInFocus]: isInFocus,
    [styles.isSelected]: isSelected && singleSelect,
    [styles.isDisabled]: isDisabled,
  });

  const listItem = renderMethod(item, {
    isInFocus,
    isSelected,
    isDisabled,
    hint,
    hintTooltip,
  });

  return (
    <li className={liClass}>
      {React.cloneElement(listItem, {
        'data-item': true,
        onClick: handleClick,
        onMouseEnter: handleMouseEnter,
      })}
      {subItems?.length ? <ul className={styles.ul}>{subItems}</ul> : null}
    </li>
  );
};

export default FilterListItem;
